import React from 'react';
import { getIn, useField, useFormikContext } from 'formik';
import { Flex } from 'lib/components/styles/layout';
import styled, { CSSProperties } from 'styled-components/macro';
import { EnhancedColorPicker } from 'app/pages/video/videoDetails/components';
import { CustomInputFieldProps } from 'app/pages/video/videoDetails/components/EnhancedColorPicker';
import { calculateHexAlpha } from 'lib/utils/annotations';
import { calculateOpacityFromHexAlpha } from 'app/pages/admin/landing-pages-v2/helpers';
import { FormikLabel } from './FormikLabel';
import {
  ColorBlock,
  ColorInput,
  OpacityDisplay,
} from 'app/pages/admin/landing-pages-v2/components/form/styles';
import { ILandingPageBuilderForm } from 'app/pages/admin/landing-pages-v2/types';

const ColorPickerContainer = styled.div`
  position: relative;
  width: 100%;
`;

interface IFormikColorPickerProps {
  name: string;
  label: string;
  isRequired?: boolean;
  disabled?: boolean;
  info?: string | React.ReactNode;
  onChange?: (color: string) => void;
  readonly?: boolean;
  onInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  extendStyles?: {
    errorWrapper?: CSSProperties;
    labelStyles?: CSSProperties | undefined;
  };
}

export const FormikColorPicker = ({
  label,
  onChange,
  isRequired,
  disabled,
  info,
  readonly = false,
  onInputChange,
  extendStyles,
  ...props
}: IFormikColorPickerProps) => {
  const [_, __, helpers] = useField(props);
  const { values } = useFormikContext<ILandingPageBuilderForm>();
  const value = getIn(values, props.name);

  const handleChange = (option: string) => {
    if (disabled) {
      return;
    }
    if (!!onChange) {
      onChange(option);
      return;
    }
    helpers.setValue(option ? option : '');
  };

  const opacityDisplayValue = calculateOpacityFromHexAlpha(value);

  return (
    <ColorPickerContainer>
      {label && (
        <FormikLabel
          label={label}
          name={props.name}
          isRequired={isRequired}
          disabled={!!disabled}
          info={info}
          labelStyles={extendStyles?.labelStyles}
        />
      )}
      <EnhancedColorPicker
        isClearable={false}
        menuPlacement='bottom-left'
        handleColorChange={color => {
          const hexAlpha = calculateHexAlpha(color).toUpperCase();
          handleChange(hexAlpha);
        }}
        playerColor={value || ''}
        renderCustomInputField={({
          onToggle,
          value,
          onChange,
        }: CustomInputFieldProps) => {
          return (
            <Flex
              flexDirection='row'
              gap='12px'
              alignItems='center'
              width='unset'
            >
              <ColorBlock
                color={value || ''}
                onClick={onToggle}
                readonly={readonly}
              />
              <ColorInput
                onChange={e => {
                  onInputChange && onInputChange(e);
                  onChange(e);
                }}
                value={value}
                readOnly={readonly}
              />
              <OpacityDisplay readonly>
                {opacityDisplayValue ? opacityDisplayValue : 0}%
              </OpacityDisplay>
            </Flex>
          );
        }}
      />
    </ColorPickerContainer>
  );
};
